import { useState, useEffect, useRef, useCallback } from "react";
import { flushSync } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import "./Dashboard.css";
import Pricing from "../../components/popups/pricing/Pricing";
import { DashboardUploadLegalNotice } from "./UploadLegalNotice";
import { useImageContext } from "../../contexts/ImageContext";
import { clearEditorSession } from "../../state/editorStore";
import {
  getDemoSamplePool,
  DEMO_SAMPLE_POOL_KEYS,
  resolveDemoSample,
} from "../../config/dashboard/demoAssetsConfig";
import { useRandomDemoSamples } from "../../hooks/dashboard/useRandomDemoSamples";
import {
  ENTRY_SOURCE,
  entrySourceForCategoryId,
  writeToolPrepSession,
  clearToolPrepSession,
  isToolPrepKey,
} from "../../utils/editorEntrySession";
import { isAiToolComingSoon } from "../../config/dashboard/trendingToolsConfig";
import {
  consumePendingToolPopup,
  rememberPendingToolPopup,
} from "../../utils/authReturnPath";
import { useHoldDashboardToolEntry } from "../../contexts/DashboardToolEntryGate";
import { prepareDashboardToolPrep } from "./navigateToToolPrep";
import DashboardV1Layout from "./DashboardV1Layout";
import DashboardPresenceStrip from "./DashboardPresenceStrip";
import DashboardToolPrepOverlay from "./DashboardToolPrepOverlay";
import CategorySeeAllModal from "./CategorySeeAllModal";
import TrendingToolUploadModal from "./TrendingToolUploadModal";
import { TRENDING_TOOL_IDS } from "../../config/dashboard/trendingToolsConfig";
import { PASSPORT_CATEGORY_ID } from "../../utils/passportPresetUtils";
import {
  flattenPresetsForTrendingTool,
  getTrendingToolPresetCategoryIds,
} from "../../config/dashboard/trendingToolPresetConfig";
import { getResizePresets } from "../../services/api/catalog/resize_presets";
import { getPassportPresets } from "../../services/api/catalog/passport_presets";
import { syncUser } from "../../services/api/app/auth";
import { fetchAccountBootstrap } from "../../services/api/app/accountBootstrap";
import { CREDITS_BALANCE_REFRESH_EVENT } from "../../config/app/signupCredits";
import { registerLibraryUploadOnce } from "../../utils/registerLibraryUploadOnce";
import ImagePickerModal from "../../components/popups/imagePicker/ImagePickerModal";
import { useImagePickerModal } from "../../hooks/app/useImagePickerModal";
import {
  libraryItemCreationId,
  openLibraryItemInEditor,
} from "../../utils/openLibraryInEditor";
import { fetchUploadImageBlob } from "../../services/api/editor/background_removal";
import { usePricingCatalog } from "../../hooks/app/usePricingCatalog";
import {
  insufficientCreditsBanner,
  resolveToolCreditCost,
  shouldForceCreditsGate,
  toolRequiresAiCredits,
} from "../../utils/credits/dashboardAiCredits";

const Dashboard = () => {
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [pricingBannerMessage, setPricingBannerMessage] = useState(null);
  const [userCredits, setUserCredits] = useState(null);
  const [activeTrendingTool, setActiveTrendingTool] = useState(null);
  const [trendingPresets, setTrendingPresets] = useState([]);
  const [trendingPresetsLoading, setTrendingPresetsLoading] = useState(false);
  const [passportTrendingPresets, setPassportTrendingPresets] = useState([]);
  const [passportTrendingPresetsLoading, setPassportTrendingPresetsLoading] = useState(false);
  const [toolPrepKey, setToolPrepKey] = useState(null);
  const pendingPassportPresetRef = useRef(null);
  const pendingResizeEntryRef = useRef(null);
  const pendingTrendingPresetRef = useRef(null);
  const pendingEditingPanelRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { openImagePicker, imagePickerProps } = useImagePickerModal();
  const heroRemoveBgSamples = useRandomDemoSamples(
    getDemoSamplePool(DEMO_SAMPLE_POOL_KEYS.BG_REMOVE),
    { enabled: true }
  );
  const { setFile, setNormalEntry, clearImage, setEntrySource, startPassportFlow, startResizeEntryFlow, startTrendingEntryFlow } =
    useImageContext();
  const { getToken, isSignedIn, isLoaded: isAuthLoaded } = useAuth();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const { costFor } = usePricingCatalog();
  const holdToolEntry = useHoldDashboardToolEntry();

  const openToolPrepOverlay = useCallback(
    (toolKey, { clearSource = true } = {}) => {
      if (!isToolPrepKey(toolKey) || isAiToolComingSoon(toolKey)) return;
      const ok = prepareDashboardToolPrep({
        toolPrep: toolKey,
        setNormalEntry,
        clearImage,
        writeToolPrepSession,
        clearEditorSession,
        clearSource,
      });
      if (ok) setToolPrepKey(toolKey);
    },
    [clearImage, setNormalEntry]
  );

  const closeToolPrepOverlay = useCallback(() => {
    setToolPrepKey(null);
  }, []);

  // Deep-link /tools/:toolKey redirects here with openToolPrep state.
  // Welcome credits (first visit) finishes first; the tool opens after claim.
  useEffect(() => {
    const key = location.state?.openToolPrep;
    if (!isToolPrepKey(key) || isAiToolComingSoon(key)) return;
    rememberPendingToolPopup(key);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (holdToolEntry) return;
    const key = consumePendingToolPopup();
    if (!isToolPrepKey(key) || isAiToolComingSoon(key)) return;
    openToolPrepOverlay(key, { clearSource: true });
  }, [holdToolEntry, openToolPrepOverlay]);

  const navigateToEditor = (state = {}) => {
    navigate("/editor", { state });
  };

  /** Fire-and-forget Uploads library register so POST /uploads starts on dashboard pick. */
  const kickoffLibraryUpload = (file) => {
    if (!file) return;
    void (async () => {
      try {
        const token = await getToken();
        if (!token) return;
        await registerLibraryUploadOnce(token, file);
      } catch (err) {
        console.warn("[Dashboard] library upload register failed", err);
      }
    })();
  };

  const activeTrendingToolId = activeTrendingTool?.id ?? null;

  const forceOutOfCreditsPopup =
    process.env.REACT_APP_ENV === "development" &&
    process.env.REACT_APP_FORCE_OUT_OF_CREDITS_POPUP === "true";

  const mockZeroCredits =
    process.env.REACT_APP_ENV === "development" &&
    process.env.REACT_APP_MOCK_ZERO_CREDITS === "true";

  useEffect(() => {
    if (!isAuthLoaded || !isSignedIn) {
      setUserCredits(null);
      return;
    }

    let cancelled = false;

    const fetchCredits = async () => {
      try {
        const token = await getTokenRef.current();
        if (!token || cancelled) return;

        try {
          await syncUser(token);
        } catch {
          // balance fetch may still work if user already exists
        }

        const data = await fetchAccountBootstrap(token);
        if (!cancelled) {
          setUserCredits(data?.credits ?? 0);
        }
      } catch {
        if (!cancelled) {
          setUserCredits(null);
        }
      }
    };

    fetchCredits();

    return () => {
      cancelled = true;
    };
  }, [isAuthLoaded, isSignedIn]);

  useEffect(() => {
    const onCreditsRefresh = (event) => {
      const balance = event?.detail?.balance;
      if (typeof balance === "number") {
        setUserCredits(balance);
        return;
      }
      (async () => {
        try {
          const token = await getTokenRef.current();
          if (!token) return;
          const data = await fetchAccountBootstrap(token, { force: true });
          setUserCredits(data?.credits ?? 0);
        } catch {
          // keep last known balance
        }
      })();
    };

    window.addEventListener(CREDITS_BALANCE_REFRESH_EVENT, onCreditsRefresh);
    return () => window.removeEventListener(CREDITS_BALANCE_REFRESH_EVENT, onCreditsRefresh);
  }, []);

  const openInsufficientCreditsPricing = (toolLabel) => {
    setPricingBannerMessage(insufficientCreditsBanner(toolLabel));
    setShowPricingModal(true);
  };

  const refreshCreditsBalance = async () => {
    try {
      const token = await getToken();
      if (!token) return;
      const data = await fetchAccountBootstrap(token, { force: true });
      setUserCredits(data?.credits ?? 0);
    } catch {
      // keep last known balance
    }
  };

  /** AI / trending / hero BG remove — editing + document tools are free. */
  const guardAiCredits = async (toolOrKey, toolLabel) => {
    if (
      forceOutOfCreditsPopup ||
      mockZeroCredits ||
      shouldForceCreditsGate(toolOrKey)
    ) {
      openInsufficientCreditsPricing(toolLabel);
      return false;
    }

    const needed = resolveToolCreditCost(costFor, toolOrKey);
    let credits = userCredits;
    if (credits === null || credits < needed) {
      try {
        const token = await getToken();
        if (token) {
          const data = await fetchAccountBootstrap(token);
          credits = data?.credits ?? 0;
          setUserCredits(credits);
        }
      } catch {
        return true;
      }
    }

    if (credits !== null && credits < needed) {
      openInsufficientCreditsPricing(toolLabel);
      return false;
    }
    return true;
  };

  const guardToolEntry = async (tool) => {
    if (!toolRequiresAiCredits(tool)) return true;
    const label =
      tool?.cardLabel || tool?.label || tool?.modalTitle || tool?.name;
    return guardAiCredits(tool, label);
  };

  const beginEditorFromFile = (
    file,
    entrySource = ENTRY_SOURCE.NORMAL,
    { skipLibraryRegister = false } = {}
  ) => {
    clearEditorSession();
    // Stale editor tool-prep session would defer BG removal on /editor.
    clearToolPrepSession();
    flushSync(() => {
      setNormalEntry();
      if (entrySource !== ENTRY_SOURCE.NORMAL) {
        setEntrySource(entrySource);
      }
      setFile(file);
    });
    if (!skipLibraryRegister) {
      kickoffLibraryUpload(file);
    }
    navigateToEditor({ entrySource, freshUpload: true });
  };

  const handleLibraryPick = (item) => {
    pendingPassportPresetRef.current = null;
    pendingResizeEntryRef.current = null;
    pendingTrendingPresetRef.current = null;
    pendingEditingPanelRef.current = null;
    setActiveTrendingTool(null);

    // Hero "Upload Image" = remove-bg flow. Uploads-library originals are not
    // cutouts yet — load the file and run the same BG-removal path as a new pick.
    const isUploadOriginal =
      item?.kind === "upload" || item?.file_type === "library_upload";
    if (isUploadOriginal) {
      void (async () => {
        try {
          const token = await getToken();
          const rootId = libraryItemCreationId(item);
          if (!token || !rootId) {
            openLibraryItemInEditor(navigate, item, {
              setNormalEntry,
              clearImage,
            });
            return;
          }
          const blob = await fetchUploadImageBlob(token, rootId);
          if (!blob || (blob.type && String(blob.type).includes("json"))) {
            throw new Error("Could not load upload image");
          }
          const name =
            item.file_name || item.label || item.name || "upload.png";
          const file = new File([blob], name, {
            type: blob.type || "image/png",
          });
          beginEditorFromFile(file, ENTRY_SOURCE.NORMAL, {
            skipLibraryRegister: true,
          });
        } catch (err) {
          console.error("Failed to open upload for background removal", err);
          openLibraryItemInEditor(navigate, item, {
            setNormalEntry,
            clearImage,
          });
        }
      })();
      return;
    }

    openLibraryItemInEditor(navigate, item, { setNormalEntry, clearImage });
  };

  const openPickerForFile = (onPickFile) => {
    openImagePicker({
      onPickFile,
      onPickLibrary: handleLibraryPick,
    });
  };

  const onUploadClick = async () => {
    if (!(await guardAiCredits("remove_bg", "AI remove background"))) return;
    openPickerForFile((file) => {
      try {
        beginEditorFromFile(file, ENTRY_SOURCE.NORMAL);
      } catch (err) {
        console.error("Failed to process file", err);
        alert("Could not process the selected file. Please try a different image.");
      }
    });
  };

  const handleTrendingToolSelect = (tool) => {
    if (!tool?.clickable) return;
    setActiveTrendingTool(tool);
  };

  const processEditingToolFile = async (file, editorPanel) => {
    if (!file) return;
    if (
      toolRequiresAiCredits(editorPanel) &&
      !(await guardAiCredits(editorPanel))
    ) {
      pendingEditingPanelRef.current = null;
      return;
    }
    try {
      clearEditorSession();
      flushSync(() => {
        setNormalEntry();
        setFile(file);
      });
      kickoffLibraryUpload(file);
      if (isToolPrepKey(editorPanel)) {
        openToolPrepOverlay(editorPanel, { clearSource: false });
      } else {
        clearToolPrepSession();
        navigateToEditor({
          entrySource: ENTRY_SOURCE.NORMAL,
          freshUpload: true,
          editorPanel: editorPanel || null,
          openEditorPanel: editorPanel || null,
        });
      }
    } catch (err) {
      console.error("Failed to process editing tool file", err);
      alert("Could not process the selected file. Please try a different image.");
    } finally {
      pendingEditingPanelRef.current = null;
    }
  };

  const handleEditingToolSelect = async (tool) => {
    if (!tool?.clickable) return;
    if (!(await guardToolEntry(tool))) return;
    pendingEditingPanelRef.current = tool.editorPanel || tool.toolKey || null;
    const editorPanel = pendingEditingPanelRef.current;

    // Prep-first: open tool popup; ImagePicker lives on the Original card.
    if (isToolPrepKey(editorPanel)) {
      pendingEditingPanelRef.current = null;
      try {
        openToolPrepOverlay(editorPanel, { clearSource: true });
      } catch (err) {
        console.error("Failed to open tool prep", err);
        alert("Could not open that tool. Please try again.");
      }
      return;
    }

    openPickerForFile((file) => processEditingToolFile(file, editorPanel));
  };

  const isPassportTrendingModal =
    activeTrendingToolId === TRENDING_TOOL_IDS.PASSPORT_PHOTOS;

  useEffect(() => {
    if (!activeTrendingToolId) {
      setTrendingPresets([]);
      setTrendingPresetsLoading(false);
      setPassportTrendingPresets([]);
      setPassportTrendingPresetsLoading(false);
      return;
    }

    if (isPassportTrendingModal) {
      setTrendingPresets([]);
      setTrendingPresetsLoading(false);

      let cancelled = false;
      setPassportTrendingPresetsLoading(true);

      getPassportPresets()
        .then((res) => {
          if (cancelled) return;
          if (res?.success && Array.isArray(res.data)) {
            const match = res.data.find((c) => c.category_id === PASSPORT_CATEGORY_ID);
            setPassportTrendingPresets(match?.presets ?? res.data[0]?.presets ?? []);
          } else {
            setPassportTrendingPresets([]);
          }
        })
        .catch(() => {
          if (!cancelled) setPassportTrendingPresets([]);
        })
        .finally(() => {
          if (!cancelled) setPassportTrendingPresetsLoading(false);
        });

      return () => {
        cancelled = true;
      };
    }

    setPassportTrendingPresets([]);
    setPassportTrendingPresetsLoading(false);

    const categoryIds =
      activeTrendingTool?.presetCategoryIds ||
      getTrendingToolPresetCategoryIds(activeTrendingToolId);
    if (!categoryIds?.length) {
      setTrendingPresets([]);
      setTrendingPresetsLoading(false);
      return;
    }

    let cancelled = false;
    setTrendingPresetsLoading(true);

    getResizePresets({ categoryIds })
      .then((res) => {
        if (cancelled) return;
        if (res?.success && Array.isArray(res.data)) {
          setTrendingPresets(flattenPresetsForTrendingTool(res.data, categoryIds));
        } else {
          setTrendingPresets([]);
        }
      })
      .catch(() => {
        if (!cancelled) setTrendingPresets([]);
      })
      .finally(() => {
        if (!cancelled) setTrendingPresetsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeTrendingToolId, activeTrendingTool, isPassportTrendingModal]);

  const handleTrendingToolUpload = async (preset) => {
    if (
      !(await guardAiCredits(
        activeTrendingTool,
        activeTrendingTool?.cardLabel || activeTrendingTool?.modalTitle
      ))
    ) {
      return;
    }
    if (!activeTrendingTool?.entrySource) return;
    pendingTrendingPresetRef.current = preset || null;
    const entrySource = activeTrendingTool.entrySource;
    const pendingPreset = pendingTrendingPresetRef.current;
    openPickerForFile(async (file) => {
      if (
        !(await guardAiCredits(
          activeTrendingTool,
          activeTrendingTool?.cardLabel || activeTrendingTool?.modalTitle
        ))
      ) {
        pendingTrendingPresetRef.current = null;
        return;
      }
      try {
        setActiveTrendingTool(null);
        clearEditorSession();
        if (pendingPreset) {
          startTrendingEntryFlow(pendingPreset, entrySource);
        } else {
          setNormalEntry();
          setEntrySource(entrySource);
        }
        setFile(file);
        clearToolPrepSession();
        kickoffLibraryUpload(file);
        navigateToEditor({
          entrySource,
          entryResizePreset: pendingPreset || null,
          openResizePanel: Boolean(pendingPreset),
          freshUpload: true,
        });
      } catch (err) {
        console.error("Failed to process trending tool file", err);
        alert("Could not process the selected file. Please try a different image.");
      } finally {
        pendingTrendingPresetRef.current = null;
      }
    });
  };

  const handlePassportPresetSelect = async (preset) => {
    if (
      !(await guardAiCredits(
        TRENDING_TOOL_IDS.PASSPORT_PHOTOS,
        "Passport Photos"
      ))
    ) {
      return;
    }
    pendingPassportPresetRef.current = preset;
    openPickerForFile(async (file) => {
      const pendingPreset = pendingPassportPresetRef.current;
      if (!file || !pendingPreset) {
        pendingPassportPresetRef.current = null;
        return;
      }
      if (
        !(await guardAiCredits(
          TRENDING_TOOL_IDS.PASSPORT_PHOTOS,
          "Passport Photos"
        ))
      ) {
        pendingPassportPresetRef.current = null;
        return;
      }
      try {
        clearEditorSession();
        startPassportFlow(pendingPreset);
        setFile(file);
        kickoffLibraryUpload(file);
        navigateToEditor({
          entrySource: ENTRY_SOURCE.PASSPORT,
          editorFlow: "passport",
          passportPreset: pendingPreset,
          freshUpload: true,
        });
      } catch (err) {
        console.error("Failed to process passport file", err);
        alert("Could not process the selected file. Please try a different image.");
        setNormalEntry();
      } finally {
        pendingPassportPresetRef.current = null;
      }
    });
  };

  const handleResizeEntryPresetSelect = async (preset, categoryId) => {
    const source = entrySourceForCategoryId(categoryId);
    if (!preset || !source) return;
    pendingResizeEntryRef.current = { preset, entrySource: source };
    openPickerForFile(async (file) => {
      const pending = pendingResizeEntryRef.current;
      if (!file || !pending?.preset || !pending?.entrySource) {
        pendingResizeEntryRef.current = null;
        return;
      }
      try {
        clearEditorSession();
        startResizeEntryFlow(pending.preset, pending.entrySource);
        setFile(file);
        clearToolPrepSession();
        kickoffLibraryUpload(file);
        navigateToEditor({
          entrySource: pending.entrySource,
          entryResizePreset: pending.preset,
          openResizePanel: true,
          freshUpload: true,
        });
      } catch (err) {
        console.error("Failed to process resize entry file", err);
        alert("Could not process the selected file. Please try a different image.");
        setNormalEntry();
      } finally {
        pendingResizeEntryRef.current = null;
      }
    });
  };

  const handleSampleClick = (sampleOrSrc, options = {}) => {
    const sample = resolveDemoSample(sampleOrSrc);
    if (!sample) return;

    setActiveTrendingTool(null);
    clearEditorSession();
    clearImage();

    const isPassportSample =
      sample.category === DEMO_SAMPLE_POOL_KEYS.PASSPORT ||
      options.categoryId === PASSPORT_CATEGORY_ID;

    if (isPassportSample) {
      const preset =
        options.preset ||
        passportTrendingPresets.find((p) => p.is_default) ||
        passportTrendingPresets[0];
      if (preset) {
        startPassportFlow(preset);
      } else {
        setNormalEntry();
      }
      navigateToEditor({
        images: [sample.originalUrl],
        demoSample: true,
        freshUpload: true,
        entrySource: ENTRY_SOURCE.PASSPORT,
        editorFlow: "passport",
        passportPreset: preset || null,
      });
      return;
    }

    setNormalEntry();
    navigateToEditor({
      images: [sample.originalUrl],
      processedImage: sample.bgRemovedUrl,
      demoSample: true,
    });
  };

  return (
    <div className="dashboard-container">
      <ImagePickerModal {...imagePickerProps} />

      <DashboardPresenceStrip />

      <header className="dashboard-container__header">
        <div className="dashboard-container__left-content">
          <div>
            <h1>Remove Image Background</h1>
            <p className="dashboard-container__header--tagline">
              Automatically erase backgrounds and highlight the subject of your
              images in a few simple steps. Quick, easy and professional results!
            </p>
          </div>
          <div className="dashboard-container__upload-section--info-text">
            <p>Supported formats: png | jpeg | jpg | webp | heic</p>
            <p>Drop an image or paste URL (upto resolution 10000 x 10000 px)</p>
            <DashboardUploadLegalNotice />
          </div>
        </div>
        
        <div className="dashboard-container__right-content">
          <div className="dashboard-container__upload-section">
            <button
              onClick={onUploadClick}
              className="dashboard-container__upload-section--upload-image-btn"
            >
              Upload Image
            </button>
          </div>
          <p className="dashboard-container--no-images-text">
            No images? Try these images
          </p>
          <div className="dashboard-container__sample-images-row">
            {heroRemoveBgSamples.map((sample) => (
              <img
                key={`${sample.id}-${sample.src}`}
                src={sample.src}
                alt={sample.alt}
                className="dashboard-container__sample-images-row--sample-thumb"
                onClick={() => handleSampleClick(sample)}
              />
            ))}
          </div>
        </div>
      </header>
      
      {/* Version 1 Dashboard Layout - Trending Tools & Categories */}
      <DashboardV1Layout
        onPassportPresetSelect={handlePassportPresetSelect}
        onResizeEntryPresetSelect={handleResizeEntryPresetSelect}
        onTrendingToolSelect={handleTrendingToolSelect}
        onEditingToolSelect={handleEditingToolSelect}
        onSampleClick={handleSampleClick}
      />

      <CategorySeeAllModal
        category={{
          category_id: PASSPORT_CATEGORY_ID,
          category_name: "Passport Photos",
        }}
        presets={passportTrendingPresets}
        isOpen={isPassportTrendingModal}
        loading={passportTrendingPresetsLoading}
        onClose={() => setActiveTrendingTool(null)}
        onUpload={(preset) => {
          setActiveTrendingTool(null);
          handlePassportPresetSelect(preset);
        }}
        onSampleClick={handleSampleClick}
      />

      <TrendingToolUploadModal
        tool={activeTrendingTool}
        isOpen={Boolean(activeTrendingTool) && !isPassportTrendingModal}
        onClose={() => setActiveTrendingTool(null)}
        onUpload={handleTrendingToolUpload}
        presets={trendingPresets}
        presetsLoading={trendingPresetsLoading}
        samplesClickable
        onSampleClick={handleSampleClick}
      />
      
      {showPricingModal && (
        <Pricing
          bannerMessage={pricingBannerMessage}
          onClose={async () => {
            setShowPricingModal(false);
            setPricingBannerMessage(null);
            await refreshCreditsBalance();
          }}
          onCreditsUpdated={refreshCreditsBalance}
        />
      )}

      {toolPrepKey ? (
        <DashboardToolPrepOverlay
          toolKey={toolPrepKey}
          onClose={closeToolPrepOverlay}
        />
      ) : null}
    </div>
  );
};

export default Dashboard;
